import React, { useState, useEffect, ReactNode, useCallback, useContext } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
// evertel
import { APIDataMedia } from '@evertel/types'
import { useService } from '@evertel/di'
import { MultilinePlaceholder, PhotoGalleryContext, CustomAsyncPaginate } from '@evertel/web/ui'
import { MultiValue, Props, SingleValue } from 'react-select'
import { NormalizedMedia, RoomMediaSearchController } from '@evertel/media'
import { Media } from '@evertel/web/ui'

interface RoomMediaSearchSelectProps extends Omit<Props, 'maxMenuHeight' | 'noOptionsMessage'> {
    roomId: number
    departmentId?: number
    selected?: APIDataMedia[]
    isDisabled?: boolean
    placeholder?: string
    searchFilter?: any
    maxMenuHeight?: number|string
    className?: string
    whereFilter?: any
    allowDownload?: boolean
    onEndReachedThreshold?: number
    noOptionsMessage?: string
    customComponents?: ReactNode
    onSelect?: (room: APIDataMedia) => void
}


const RoomMediaSearchSelect: React.FC<RoomMediaSearchSelectProps> = observer(({
    roomId,
    departmentId,
    selected = [],
    isDisabled,
    placeholder = 'Search...',
    noOptionsMessage = 'No files found',
    onSelect,
    whereFilter,
    maxMenuHeight,
    allowDownload = true,
    className,
    customComponents,
    onEndReachedThreshold = 80,
    ...otherProps
}) => {

    const roomMediaSearchController = useService(RoomMediaSearchController, [roomId])
    const photoGallery = useContext(PhotoGalleryContext)

    const [isLoading, setIsLoading] = useState(true)
    const [isSearching, setIsSearching] = useState(false)

    useEffect(() => {
        setIsLoading(true)

        if (!roomId) return

        roomMediaSearchController.init(roomId, false)
        //needs to render once with loading true to reload async paginate
        setTimeout(() => { setIsLoading(false)}, 1)

    }, [roomId])

    //This should have been triggered on the async select onChange even, but for some reason never gets called
    //onSelect called in <Media> onClick anyway
    // const onSelectOption = (media: MultiValue<APIDataMedia>|SingleValue<APIDataMedia>) => {
    //     if (onSelect) onSelect(media as APIDataMedia)
    // }

    const search = useCallback( async (
        searchString: string,
        loadedOptions: APIDataMedia[],
        { page }: { page: number }
    ) => {

        if (roomMediaSearchController.searchTerm !== searchString) {
            // only set searchTerm if it changes
            roomMediaSearchController.setSearchTerm(searchString)
        }

        //page must be set after searchTerm since searchTerm will reset page since react-select-async keeps cache
        roomMediaSearchController.setPage(page)

        try {
            setIsSearching(true)
            await roomMediaSearchController.search()
            setIsSearching(false)

        } catch (error) {
            setIsSearching(false)
            console.error('ERROR: RoomMediaSearchSelect search():', error)
        }

        // grab results and format them for the Select
        // const results = formatForSelect(controller.media, 'id', 'id')
        // const hasMore = roomMediaSearchController.media?.length < roomMediaSearchController.mediaCount
        const hasMore = (roomMediaSearchController.mediaLatestFetch.length >= roomMediaSearchController.limit)

        return {
            options: roomMediaSearchController.mediaLatestFetch || [],
            hasMore,
            additional: {
                page: page + 1 || 1
            }
        }
    }, [roomId])

    const shouldLoadMore = (scrollHeight: number, clientHeight: number, scrollTop: number): boolean => {
        if (isSearching) return false
        // a hidden (display: none) menu measures 0 for everything, which reads as "scrolled to
        // the bottom". AsyncPaginate polls this every 300ms, so without this guard a hidden tab
        // pages through every result in the background
        if (!clientHeight) return false

        const maxPixelThreshold = 300
        const percentThreshold = 100 - onEndReachedThreshold

        const pxFromBottom = scrollHeight - clientHeight - scrollTop
        const percentThresholdFromBottomInPx = ((percentThreshold / 100) * (scrollHeight - clientHeight))

        // Determine the threshold to use (whichever is smaller)
        const thresholdToUse = Math.min(percentThresholdFromBottomInPx, maxPixelThreshold)

        // console.log('px from bottom / threshold, result', pxFromBottom, thresholdToUse, pxFromBottom <= thresholdToUse)
        return pxFromBottom <= thresholdToUse
    }

    const onClickMedia = (media: NormalizedMedia) => {

        if (!media) return

        if (['image', 'video', 'audio'].includes(media.mediaType)) {
            photoGallery.setMedia([media])
            photoGallery.setVisible(true)
        }
    }

    if (isLoading) {
        return (
            <MultilinePlaceholder
                sections={2}
                className={className}
            />
        )
    }

    return (
        <CustomAsyncPaginate
            name="room-media"
            keyName={roomId}
            searchOnly={true}
            loadOptions={search}
            debounceTimeout={300}
            additional={{
                page: 1
            }}
            defaultOptions
            shouldLoadMore={shouldLoadMore}
            loadingMessage={() => <span role="alert" aria-live="polite">Loading files...</span>}
            value={selected}
            maxMenuHeight={maxMenuHeight}
            isDisabled={isDisabled}
            // onChange={onSelectOption}
            noOptionsMessage={() => <span role="alert" aria-live="polite">{noOptionsMessage}</span>}
            placeholder={placeholder}
            className={className}
            components={customComponents as any || { Option: MediaItem as any}}
            callbacks={{
                onClickMedia,
                allowDownload
            }}
            {...otherProps}
        />
    )
})

interface MediaItemProps {
    data: NormalizedMedia
    innerProps: any
    selectProps: {
        callbacks: {
            onClickMedia: (arg0: NormalizedMedia) => void
            allowDownload: boolean
        }
    }
}

//this is tightly coupled to the RoomMediaSearchSelect so should remain in this file
const MediaItem: React.FC<MediaItemProps> = observer(({
    data,
    innerProps,
    selectProps
}) => {
    //console.log(data, innerProps, selectProps)

    if (!data) return null

    const { onClickMedia, allowDownload } = selectProps.callbacks
    const thumbWidth = 50

    const isMedia = ['image', 'video', 'audio'].includes(data.mediaType)

    return (
        <Media
            media={data}
            variant="file-list"
            width={thumbWidth}
            height={thumbWidth}
            rounded
            allowDownload={allowDownload}
            className="p-1 w-100"
            onClick={isMedia ? () => onClickMedia(data) : undefined}
            videoProps={{ asThumbnail: true }}
            imageProps={{ flexibility: 'fixed' }}
            resizeMode="cover"
        />
    )
})

export {
    RoomMediaSearchSelect
}
